import type { AgentChatMessage } from "@/components/agent-session/types";

export interface DreamFinding {
  title: string;
  detail: string;
}

export interface DreamResult {
  memories: DreamFinding[];
  ci_optimizations: DreamFinding[];
  harness_improvements: DreamFinding[];
  mistakes: DreamFinding[];
  limits: string[];
}

export interface DreamReportMetadata {
  child_session_id: string;
  dream_result?: DreamResult;
}

export interface DreamReportEntry {
  child_session_id: string;
  message: {
    role: string;
    content: string;
    metadata?: Record<string, unknown> | null;
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseFindings(value: unknown): DreamFinding[] | null {
  if (!Array.isArray(value)) return null;
  return value.flatMap((finding) =>
    isRecord(finding) &&
    typeof finding.title === "string" &&
    typeof finding.detail === "string"
      ? [{ title: finding.title, detail: finding.detail }]
      : [],
  );
}

export function readDreamResult(value: unknown): DreamResult | undefined {
  if (!isRecord(value)) return undefined;
  const memories = parseFindings(value.memories);
  const ciOptimizations = parseFindings(value.ci_optimizations);
  const harnessImprovements = parseFindings(value.harness_improvements);
  const mistakes = parseFindings(value.mistakes);
  if (
    memories === null ||
    ciOptimizations === null ||
    harnessImprovements === null ||
    mistakes === null ||
    !Array.isArray(value.limits)
  ) {
    return undefined;
  }
  return {
    memories,
    ci_optimizations: ciOptimizations,
    harness_improvements: harnessImprovements,
    mistakes,
    limits: value.limits.filter(
      (limit): limit is string => typeof limit === "string",
    ),
  };
}

export function dreamReportMetadata(message: {
  role: string;
  metadata?: Record<string, unknown> | null;
}): DreamReportMetadata | null {
  if (message.role !== "agent") return null;
  const raw = message.metadata?.dream_report;
  if (!isRecord(raw) || typeof raw.child_session_id !== "string") return null;
  const dreamResult = readDreamResult(raw.dream_result);
  return {
    child_session_id: raw.child_session_id,
    ...(dreamResult ? { dream_result: dreamResult } : {}),
  };
}

// One entry per message object, so buildChildReportsIndex can tell "unchanged"
// from "changed" by reference and useChildReportsIndex settles.
const entryByMessage = new WeakMap<AgentChatMessage, DreamReportEntry | null>();

export function dreamReportEntry(
  message: AgentChatMessage,
): DreamReportEntry | null {
  let entry = entryByMessage.get(message);
  if (entry === undefined) {
    const report = dreamReportMetadata(message);
    entry = report && {
      child_session_id: report.child_session_id,
      message: {
        role: message.role,
        content: message.content,
        metadata: message.metadata,
      },
    };
    entryByMessage.set(message, entry);
  }
  return entry;
}

/** Display-only row, not a turn of this session: working-state and turn-anchoring scans skip it. */
export function isDreamReportMessage(message: {
  role: string;
  metadata?: Record<string, unknown> | null;
}): boolean {
  return dreamReportMetadata(message) !== null;
}

export function dreamFindingCount(result: DreamResult): number {
  return (
    result.memories.length +
    result.ci_optimizations.length +
    result.harness_improvements.length +
    result.mistakes.length
  );
}

export function dreamFindingsLabel(count: number): string {
  if (count === 0) return "No findings";
  if (count === 1) return "1 finding";
  return `${count} findings`;
}

export interface DreamResultSection {
  heading: string;
  markdown: string;
}

export function dreamResultSections(result: DreamResult): DreamResultSection[] {
  const sections: Array<[string, string[]]> = [
    ["Memories", result.memories.map(renderFinding)],
    ["CI optimization", result.ci_optimizations.map(renderFinding)],
    ["Harness improvements", result.harness_improvements.map(renderFinding)],
    ["Mistakes", result.mistakes.map(renderFinding)],
    ["Limits", result.limits.map((limit) => `- ${limit}`)],
  ];
  return sections
    .filter(([, lines]) => lines.length > 0)
    .map(([heading, lines]) => ({ heading, markdown: lines.join("\n") }));
}

function renderFinding({ title, detail }: DreamFinding): string {
  const punctuatedTitle = /[.!?]$/.test(title) ? title : `${title}.`;
  return `- **${punctuatedTitle}** ${detail}`;
}
